import React from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ModulePageLayout — Standard operativo v1.7.0 (22/07/2026)
 * ==========================================================
 * Struttura obbligatoria per ogni modulo della Suite Gruppo Impresa.
 *
 * Zone (dall'alto in basso):
 *   1. HEADER          altezza fissa var(--gis-mod-header-h)   = 80px
 *      icona + titolo modulo · breadcrumb opzionale · CTA Nuovo
 *   2. KPI-STRIP       altezza max var(--gis-mod-kpi-h)        = 60px
 *      barra orizzontale di indicatori sintetici (numero+label)
 *   3. TOOLBAR         altezza max var(--gis-mod-toolbar-h)    = 56px
 *      filtri su una sola riga: ricerca + select + azioni
 *   4. BODY            flex-1 · scroll interno alla tabella
 *   5. FOOTER opz.     paginazione o riepilogo (auto-height)
 *
 * Filosofia: MENO decorazioni, PIÙ operatività. La tabella riceve
 * l'80% dell'altezza disponibile. Su Full HD (1920×1080) restano
 * visibili 15-20 righe senza scroll di pagina.
 *
 * Props:
 *   - icon:          componente lucide-react (h-5 w-5)
 *   - title:         titolo del modulo (richiesto)
 *   - subtitle:      sottotitolo compatto opzionale (una riga)
 *   - breadcrumb:    array di stringhe [{ label, onClick? }] opzionale
 *   - primaryAction: { label, onClick, icon?, testid?, disabled? }
 *                    (bottone "+ Nuovo …" a destra dell'header)
 *   - actions:       ReactNode extra a destra dell'header (Importa/Esporta)
 *   - kpiStrip:      ReactNode compatto (usa <KpiPill /> per uniformità)
 *   - toolbar:       ReactNode compatto (ricerca + selects + reset)
 *   - children:      area operativa (tabella / lista)
 *   - footer:        ReactNode footer opzionale (paginazione)
 *   - testid:        data-testid del contenitore radice
 */
export function ModulePageLayout({
  icon: Icon,
  title,
  subtitle,
  breadcrumb,
  primaryAction,
  actions,
  kpiStrip,
  toolbar,
  children,
  footer,
  className,
  testid,
}) {
  return (
    <div
      className={cn("module-page-layout flex flex-col", className)}
      data-testid={testid}
      style={{ gap: "var(--gis-mod-gap)" }}
    >
      {/* ── HEADER (max 80px) ───────────────────────────── */}
      <header
        className="module-page-header flex items-center justify-between gap-3 px-1"
        style={{ minHeight: "var(--gis-mod-header-h)", maxHeight: "var(--gis-mod-header-h)" }}
        data-testid={testid ? `${testid}-header` : "module-header"}
      >
        <div className="min-w-0 flex-1 flex items-center gap-3">
          {Icon && (
            <span
              className="shrink-0 h-10 w-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: "var(--gis-green-50)" }}
            >
              <Icon className="h-5 w-5" style={{ color: "var(--gis-green)" }} />
            </span>
          )}
          <div className="min-w-0">
            {breadcrumb && breadcrumb.length > 0 && (
              <div className="flex items-center gap-1 text-[11px] gis-body text-slate-500 truncate">
                {breadcrumb.map((b, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-slate-300">›</span>}
                    {b.onClick ? (
                      <button
                        type="button"
                        onClick={b.onClick}
                        className="hover:text-[var(--gis-green)] transition-colors"
                      >
                        {b.label}
                      </button>
                    ) : (
                      <span>{b.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            <h1
              className="gis-heading truncate text-slate-900 font-bold"
              style={{ fontSize: "20px", lineHeight: "1.2" }}
              data-testid={testid ? `${testid}-title` : "module-title"}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="gis-body text-xs text-slate-500 truncate mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className="gis-btn-primary h-9"
              data-testid={primaryAction.testid || "module-primary-action"}
            >
              {primaryAction.icon ? (
                <primaryAction.icon className="h-4 w-4 mr-1.5" />
              ) : (
                <Plus className="h-4 w-4 mr-1.5" />
              )}
              {primaryAction.label}
            </Button>
          )}
        </div>
      </header>

      {/* ── KPI-STRIP (max 60px) ────────────────────────── */}
      {kpiStrip && (
        <section
          className="module-page-kpi flex items-center gap-2 overflow-x-auto"
          style={{ minHeight: "var(--gis-mod-kpi-h)", maxHeight: "var(--gis-mod-kpi-h)" }}
          data-testid={testid ? `${testid}-kpi` : "module-kpi"}
        >
          {kpiStrip}
        </section>
      )}

      {/* ── TOOLBAR (max 56px) ──────────────────────────── */}
      {toolbar && (
        <section
          className="module-page-toolbar flex items-center gap-2 flex-wrap"
          style={{
            minHeight: "var(--gis-mod-toolbar-h)",
            maxHeight: "var(--gis-mod-toolbar-h)",
          }}
          data-testid={testid ? `${testid}-toolbar` : "module-toolbar"}
        >
          {toolbar}
        </section>
      )}

      {/* ── BODY (occupa il resto) ──────────────────────── */}
      <section
        className="module-page-body flex-1 min-h-0"
        data-testid={testid ? `${testid}-body` : "module-body"}
      >
        {children}
      </section>

      {/* ── FOOTER opz. (paginazione) ───────────────────── */}
      {footer && (
        <footer
          className="module-page-footer"
          data-testid={testid ? `${testid}-footer` : "module-footer"}
        >
          {footer}
        </footer>
      )}
    </div>
  );
}

/**
 * <KpiPill /> — pillola KPI compatta da usare nella `kpiStrip`.
 * Layout orizzontale: numero grande + label piccola.
 *
 * Props:
 *   - value:  numero/stringa da mostrare (grande)
 *   - label:  etichetta breve (all-caps ridotta)
 *   - tone:   'default' | 'success' | 'warning' | 'danger' | 'info' | 'brand'
 *   - active: se true evidenzia (bordo verde brand + bg tint)
 *   - onClick opzionale per usarla come filtro
 *   - icon:   componente lucide opzionale
 */
export function KpiPill({
  value,
  label,
  tone = "default",
  active = false,
  onClick,
  icon: Icon,
  testid,
  className,
}) {
  const isButton = typeof onClick === "function";
  const Tag = isButton ? "button" : "div";

  const toneColor = {
    default: "#334155",
    success: "var(--gis-green)",
    warning: "var(--gis-gold-dark)",
    danger: "#dc2626",
    info: "#0284c7",
    brand: "var(--gis-green)",
  }[tone] || "#334155";

  return (
    <Tag
      type={isButton ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "kpi-pill shrink-0 flex items-center gap-2 rounded-lg border transition-all bg-white",
        active
          ? "border-[var(--gis-green)] shadow-sm"
          : "border-slate-200 hover:border-slate-300",
        isButton && "cursor-pointer",
        className,
      )}
      style={{
        padding: "6px 12px",
        height: 44,
        backgroundColor: active ? "var(--gis-green-50)" : "white",
      }}
      data-testid={testid}
    >
      {Icon && (
        <Icon
          className="h-3.5 w-3.5 shrink-0"
          style={{ color: toneColor }}
        />
      )}
      <span
        className="gis-heading font-bold leading-none"
        style={{ color: toneColor, fontSize: 18 }}
      >
        {value}
      </span>
      <span
        className="gis-heading-wide text-[10px] leading-tight text-slate-500 whitespace-nowrap"
        style={{ letterSpacing: "0.06em" }}
      >
        {label}
      </span>
    </Tag>
  );
}

export default ModulePageLayout;

